"use client"

import { fill } from "../../lib/fill"
import { useTr } from "../../lib/use-tr"
import { ChevronDown, Loader2 } from "lucide-react"
import { useState } from "react"

import {
  DISQUALIFY_REASONS,
  DISQUALIFY_REASON_LABEL,
  DISQUALIFY_T,
  LOST_REASONS,
  isCompanyReason,
} from "@/lib/apps/autoleads/disqualify"
import { useSetAccountStatus } from "@/lib/apps/autoleads/hooks/use-crm-mutations"
import {
  CLOSED_LEAD_CATEGORIES,
  LEAD_CATEGORY_META,
  LEAD_CATEGORY_T,
} from "@/lib/apps/autoleads/lead-category"
import { cn } from "@/lib/utils"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Field,
  FieldDescription,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from "@/components/ui/field"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Textarea } from "@/components/ui/textarea"

import { CategoryBadge } from "./category-badge"
import type { LeadCategory } from "@/nango/types/apps.autoleads/autoleads/AutoLeads"
import type { CrmAccountDisqualifyReason } from "@/nango/types/apps.autoleads/autoleads/Crm"

const LEAD_STATUS_T = {
  statusPickerLabel: { FR: "Changer le statut", EN: "Change the status" },
  statusLost: { FR: "Perdu", EN: "Lost" },
  statusChurned: { FR: "Client perdu", EN: "Churned client" },
  statusChangedToast: { FR: "Statut mis à jour.", EN: "Status updated." },
  disqualifyTitle: {
    FR: "Marquer l'entreprise comme perdue",
    EN: "Mark the company as lost",
  },
  churnTitle: {
    FR: "Marquer comme client perdu",
    EN: "Mark as a churned client",
  },
  disqualifyDesc: {
    FR: "{company} sort de toutes ses campagnes : plus personne n'y sera contacté. C'est irréversible : elle ne pourra plus repasser en prospect.",
    EN: "{company} leaves every campaign: nobody there is contacted again. This cannot be undone: it can never go back to prospect.",
  },
  confirmTitle: {
    FR: "Passer {company} en {status} ?",
    EN: "Move {company} to {status}?",
  },
  confirmDesc: {
    FR: "C'est irréversible : la séquence de prospection s'arrête dans toutes ses campagnes et l'entreprise ne pourra plus repasser en prospect. Vous pourrez toujours la passer en chaud, client ou perdu.",
    EN: "This cannot be undone: the prospecting sequence stops in every campaign and the company can never go back to prospect. You can still move it between hot, client and lost.",
  },
  confirmAction: { FR: "Confirmer", EN: "Confirm" },
  churnDesc: {
    FR: "{company} n'est plus client, dans toutes ses campagnes. Vous pourrez le repasser en chaud ou en client.",
    EN: "{company} is no longer a client, in every campaign. You can move it back to hot or client.",
  },
  disqualifyReason: { FR: "Motif", EN: "Reason" },
  disqualifyNote: { FR: "Précisions", EN: "Details" },
  disqualifyNotePlaceholder: {
    FR: "Ce qui s'est passé, en une phrase",
    EN: "What happened, in one sentence",
  },
  disqualifyNoteRequired: {
    FR: "Obligatoire pour ce motif.",
    EN: "Required for this reason.",
  },
  disqualifyNoteOptional: { FR: "Facultatif.", EN: "Optional." },
  cancel: { FR: "Annuler", EN: "Cancel" },
}

// A company still being worked is written off with the pipeline's motives; one
// that already talked to us is lost with the deal's. Bad timing needs the
// contact a rep spoke to, so it stays on the campaign board.
const PROSPECT_REASONS = DISQUALIFY_REASONS.filter(
  (reason): reason is CrmAccountDisqualifyReason & typeof reason =>
    reason !== "bad_timing" && isCompanyReason(reason),
)

// The statuses a rep picks by hand; the menu shows them as the pills they set.
const PICKABLE_STATUSES = ["hot", "client"] as const

const NOTE_REQUIRED: readonly CrmAccountDisqualifyReason[] = [
  "other",
  "lost_other",
]

function DisqualifyDialog({
  open,
  onOpenChange,
  accountId,
  accountName,
  category,
  pending,
  onSubmit,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  accountId: number
  accountName: string
  category: LeadCategory
  pending: boolean
  onSubmit: (reason: CrmAccountDisqualifyReason, note: string) => void
}) {
  const tr = useTr(LEAD_STATUS_T)
  const trReason = useTr(DISQUALIFY_T)
  const [reason, setReason] = useState<CrmAccountDisqualifyReason | "">("")
  const [note, setNote] = useState("")
  const churn = category === "client"
  const reasons: readonly CrmAccountDisqualifyReason[] =
    category === "hot" || churn ? LOST_REASONS : PROSPECT_REASONS
  const noteRequired = reason !== "" && NOTE_REQUIRED.includes(reason)
  const ready = reason !== "" && (!noteRequired || Boolean(note.trim()))

  function close(next: boolean) {
    // The next company is a different one: a pre-filled motive is a motive
    // nobody chose.
    if (!next) {
      setReason("")
      setNote("")
    }
    onOpenChange(next)
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-lg md:max-w-lg lg:max-w-lg xl:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {tr(churn ? "churnTitle" : "disqualifyTitle")}
          </DialogTitle>
          <DialogDescription>
            {tr(churn ? "churnDesc" : "disqualifyDesc").replace(
              "{company}",
              accountName,
            )}
          </DialogDescription>
        </DialogHeader>

        <FieldSet>
          <FieldLegend variant="label">{tr("disqualifyReason")}</FieldLegend>
          <RadioGroup
            value={reason}
            onValueChange={(value) =>
              setReason(value as CrmAccountDisqualifyReason)
            }
            className="grid gap-2 sm:grid-cols-2"
          >
            {reasons.map((one) => (
              <Field key={one} orientation="horizontal">
                <RadioGroupItem
                  value={one}
                  id={`account-dq-${accountId}-${one}`}
                />
                <FieldLabel
                  htmlFor={`account-dq-${accountId}-${one}`}
                  className="font-normal"
                >
                  {trReason(DISQUALIFY_REASON_LABEL[one])}
                </FieldLabel>
              </Field>
            ))}
          </RadioGroup>
        </FieldSet>

        {reason ? (
          <Field>
            <FieldLabel htmlFor={`account-dq-${accountId}-note`}>
              {tr("disqualifyNote")}
            </FieldLabel>
            <Textarea
              id={`account-dq-${accountId}-note`}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder={tr("disqualifyNotePlaceholder")}
              className="min-h-16 resize-none"
            />
            <FieldDescription>
              {tr(
                noteRequired
                  ? "disqualifyNoteRequired"
                  : "disqualifyNoteOptional",
              )}
            </FieldDescription>
          </Field>
        ) : null}

        <DialogFooter>
          <Button variant="ghost" onClick={() => close(false)}>
            {tr("cancel")}
          </Button>
          <Button
            variant="destructive"
            disabled={pending || !ready}
            onClick={() => reason && onSubmit(reason, note)}
          >
            {pending ? <Loader2 className="size-3.5 animate-spin" /> : null}
            {tr(churn ? "statusChurned" : "statusLost")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// The company's category, and the way to change it from anywhere a lead shows:
// hot, client, lost, or churned once it was a client. "Prospect" is where a
// company starts and is never chosen by hand. A closed company can be moved
// back to hot or client. Acts on the CRM company, so every campaign copy follows.
export function LeadStatusMenu({
  accountId,
  accountName,
  category,
  size = "default",
  className,
}: {
  accountId: number
  accountName: string
  category: LeadCategory
  size?: "default" | "sm"
  className?: string
}) {
  const tr = useTr(LEAD_STATUS_T)
  const trCategory = useTr(LEAD_CATEGORY_T)
  const [disqualifying, setDisqualifying] = useState(false)
  // Leaving "prospect" ends the sequence for good, so that one step asks first.
  const [confirming, setConfirming] = useState<"hot" | "client" | null>(null)
  const setStatus = useSetAccountStatus({
    successMessage: tr("statusChangedToast"),
    onSuccess: () => {
      setDisqualifying(false)
      setConfirming(null)
    },
  })
  const closed = CLOSED_LEAD_CATEGORIES.includes(category)
  const churn = category === "client"
  const meta = LEAD_CATEGORY_META[category]

  return (
    // Rows and cards open on click: picking a status must not open them too.
    // React bubbles portal events through this tree, so the menu and the
    // dialog are covered as well.
    <span
      className={cn("inline-flex", className)}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <DropdownMenu>
        {/* The pill is the control: one surface, its chevron inside. */}
        <DropdownMenuTrigger asChild disabled={setStatus.isPending}>
          <Badge
            asChild
            variant={meta.variant}
            tone={meta.tone}
            interactive
            className={
              size === "sm"
                ? "h-6 gap-1.5 px-2 font-normal"
                : "h-7 gap-1.5 px-2.5 font-normal"
            }
          >
            <button
              type="button"
              aria-label={`${tr("statusPickerLabel")}: ${trCategory(meta.labelKey)}`}
            >
              {setStatus.isPending ? (
                <Loader2 className="animate-spin" />
              ) : (
                <meta.Icon className={meta.iconClassName} />
              )}
              {trCategory(meta.labelKey)}
              <ChevronDown className="opacity-60" />
            </button>
          </Badge>
        </DropdownMenuTrigger>
        {/* Hugs its pills: the menu is a short list, not a column. */}
        <DropdownMenuContent align="start" className="w-auto min-w-0">
          <DropdownMenuRadioGroup
            value={category}
            onValueChange={(value) => {
              const status = value as (typeof PICKABLE_STATUSES)[number]
              if (status === category) return
              if (category === "prospect") setConfirming(status)
              else setStatus.mutate({ accountId, status })
            }}
          >
            {PICKABLE_STATUSES.map((status) => (
              <DropdownMenuRadioItem
                key={status}
                value={status}
                variant="badge"
              >
                <CategoryBadge category={status} />
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
          {closed ? null : (
            <>
              <DropdownMenuSeparator />
              {/* Opens the motive dialog, hence the ellipsis. Quiet like the
                  "lost" pill: the dialog carries the warning. */}
              <DropdownMenuItem
                variant="badge"
                onSelect={() => setDisqualifying(true)}
              >
                <CategoryBadge category={churn ? "churned" : "lost"}>
                  …
                </CategoryBadge>
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <AlertDialog
        open={confirming !== null}
        onOpenChange={(open) => !open && setConfirming(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {fill(tr("confirmTitle"), {
                company: accountName,
                status: confirming
                  ? trCategory(LEAD_CATEGORY_META[confirming].labelKey)
                  : "",
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>{tr("confirmDesc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tr("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              disabled={setStatus.isPending}
              onClick={(event) => {
                // Stays open until the change lands: onSuccess closes it.
                event.preventDefault()
                if (confirming)
                  setStatus.mutate({ accountId, status: confirming })
              }}
            >
              {setStatus.isPending ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : null}
              {tr("confirmAction")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <DisqualifyDialog
        open={disqualifying}
        onOpenChange={setDisqualifying}
        accountId={accountId}
        accountName={accountName}
        category={category}
        pending={setStatus.isPending}
        onSubmit={(reason, note) =>
          setStatus.mutate({
            accountId,
            status: "disqualified",
            reason,
            note,
          })
        }
      />
    </span>
  )
}
